import { useState, useContext, useCallback, useRef } from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { Navigate, useLocation, Link } from 'react-router-dom'

import { AppContext } from './AppContext.tsx'
import LoadingScreen from './LoadingScreen.jsx'
import Spinner from './Spinner.jsx'

import RecoveryKeyModal from './RecoveryKeyModal.tsx'
import Logo from './logo/Logo.tsx'
import { useCrypto } from '../hooks/useCrypto.ts'
import { 
  generateRecoveryKey, 
  generateMasterKey,
  derivePasswordKey,
  wrapMasterKeyWithPassword,
  wrapMasterKeyWithRecovery,
} from '../lib/crypto'
import { isElectron } from '../utils/platform.js'

const Login = () => {
  const {
    supabaseClient,
    onAuthSessionUpdate,
    authSession,
    authLoading,
    initializingUI
  } = useContext(AppContext)
  const { unlock, setMasterKey } = useCrypto()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/chat'
    
  const [phase, setPhase] = useState('start')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [recoveryKey, setRecoveryKey] = useState(null)
  const [pendingSession, setPendingSession] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const nodeRef = useRef(null)
  const contentNodeRef = useRef(null)

  const handleSignIn = useCallback(async () => {
    const newErrors = {}
    if (!email || !email.includes('@')) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!password || password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    setErrors({})

    try {
      const { data: { session }, error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setErrors({ form: 'Invalid email or password' })
        } else {
          setErrors({ form: error.message })
        }
        return
      }

      // Unlock crypto vault - fetches wrapped key from server and unwraps with password
      await unlock(password, email, session.access_token)
      onAuthSessionUpdate(session)
    } catch {
      setErrors({ form: 'Sign in failed. Please try again.' })
    } finally {
      setLoading(false)
    }
  }, [email, password, supabaseClient, unlock, onAuthSessionUpdate])

  const handleSignUp = useCallback(async () => {
    const newErrors = {}
    if (!email || !email.includes('@')) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!password || password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    setErrors({})

    try {
      const { data: { session }, error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined,
        },
      })

      if (error) {
        if (error.code === 'signup_disabled') {
          setErrors({ form: 'Consl is currently still in an early stage. More coming soon.' })
        } else if (error.message.includes('already registered')) {
          setErrors({ email: 'An account with this email already exists' })
        } else {
          setErrors({ form: error.message })
        }
        return
      }

      // Generate random master key (not derived from password)
      const masterKey = generateMasterKey()
      
      // Store master key in crypto context
      await setMasterKey(masterKey)
      
      // Generate recovery key
      const { displayKey } = generateRecoveryKey()
      
      // Wrap master key with both password and recovery key
      const passwordKey = await derivePasswordKey(password, email)
      const wrappedByPassword = await wrapMasterKeyWithPassword(masterKey, passwordKey)
      const wrappedByRecovery = await wrapMasterKeyWithRecovery(masterKey, displayKey)
      
      // Store both wrapped keys on server
      fetch(`${import.meta.env.VITE_API_URL}/api/recovery-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          wrapped_by_password: wrappedByPassword,
          wrapped_by_recovery: wrappedByRecovery,
        }),
      }).catch(err => {
        console.warn('Failed to store wrapped keys on server:', err)
      })
      
      setRecoveryKey(displayKey)
      setPendingSession(session)
      setPhase('recovery')
    } catch {
      setErrors({ form: 'Sign up failed. Please try again.' })
    } finally {
      setLoading(false)
    }
  }, [email, password, confirmPassword, supabaseClient, setMasterKey])

  const handleRecoveryConfirm = useCallback(() => {
    if (pendingSession) {
      onAuthSessionUpdate(pendingSession)
    }
    setRecoveryKey(null)
    setPendingSession(null)
  }, [pendingSession, onAuthSessionUpdate])

  const handleRecoveryCancel = useCallback(() => {
    supabaseClient.auth.signOut()
    setPhase('start')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setRecoveryKey(null)
    setPendingSession(null)
    setErrors({})
  }, [supabaseClient])

  const resetForm = useCallback(() => {
    setPhase('start')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setErrors({})
  }, [])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  if (authLoading) {
    return <LoadingScreen title={getGreeting()} />
  }

  if (authSession && initializingUI) {
    return <LoadingScreen title={getGreeting()} subtitle="Setting everything up..." />
  }

  if (authSession && !initializingUI) {
    return <Navigate to={from} replace />
  }

  return (
    <>
      <style>{`
        .fade-enter {
          opacity: 0;
          transform: translateY(4px) scale(0.98);
        }
        .fade-enter-active {
          opacity: 1;
          transform: translateY(0) scale(1);
          transition: opacity 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
                      transform 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .fade-exit {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .fade-exit-active {
          opacity: 0;
          transform: translateY(-4px) scale(0.98);
          transition: opacity 160ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
                      transform 160ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        @media (prefers-reduced-motion: reduce) {
          .fade-enter,
          .fade-enter-active,
          .fade-exit,
          .fade-exit-active {
            transition: none;
            transform: none;
          }
        }
      `}</style>
      {isElectron() && (
        <div
          className="fixed left-0 right-0 top-0 z-50 h-[38px] bg-transparent"
          style={{ WebkitAppRegion: 'drag' }}
          aria-hidden
        />
      )}
      <div 
        className="min-h-screen-mobile flex items-center justify-center p-4"
        style={{ backgroundImage: 'linear-gradient(in oklab 180deg, oklab(17.3% 0 0) 50%, oklab(14.5% 0 0) 100%)' }}
      >
        <div className="w-[300px]">
        <div className="text-center">
          <div className="mb-[8px] flex items-center justify-center">
            <Logo variant="wordmark" text="Consl" size={32} ariaLabel="Consl" />
          </div>
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={phase}
              nodeRef={nodeRef}
              timeout={200}
              classNames="fade"
            >
              <h1 ref={nodeRef} className="text-2xl font-normal text-primary mb-4">
                {phase === 'start' && 'Welcome to Consl'}
                {phase === 'signin' && 'Sign in to your account'}
                {phase === 'signup' && 'Create your account'}
                {phase === 'recovery' && 'Save your recovery key'}
              </h1>
            </CSSTransition>
          </SwitchTransition>
        </div>



        <SwitchTransition mode="out-in">
          <CSSTransition
            key={phase}
            nodeRef={contentNodeRef}
            timeout={200}
            classNames="fade"
          >
            <div ref={contentNodeRef}>
              {phase === 'start' && (
                <div className="flex flex-col items-center">
                  <button 
                    className="w-full h-[42px] bg-accent text-primary rounded-lg hover:bg-hover transition-colors duration-150 font-normal flex items-center justify-center mb-4 active:scale-[0.97]"
                    onClick={() => setPhase('signin')}
                  >
                    Sign in
                  </button>

                  <button
                    className="w-full h-[42px] bg-bg-panel rounded-lg hover:bg-bg-hover transition-colors duration-150 text-primary font-normal mb-4 flex items-center justify-center active:scale-[0.97]"
                    onClick={() => setPhase('signup')}
                  >
                    Create account
                  </button>
                  
                  <p className="text-sm text-secondary text-center">
                    <a href="https://www.consl.app" className="text-primary hover:underline">Learn more</a>{' '}
                    about Consl
                  </p>
                </div>
              )}

              {phase === 'signin' && (
                <form onSubmit={(e) => { e.preventDefault(); handleSignIn(); }} className="flex flex-col items-center">
                  {errors.form && (
                    <p className="w-full text-xs text-error mb-4">{errors.form}</p>
                  )}
                  
                  <div className="w-full mb-4">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: null })) }}
                      disabled={loading}
                      className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.email ? 'border-error' : 'border-border-default'}`}
                      placeholder="you@example.com"
                      autoComplete="email"
                      autoFocus
                    />
                    {errors.email && <p className="text-xs text-error mt-1">{errors.email}</p>}
                  </div>

                  <div className="w-full mb-4">
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: null })) }}
                      disabled={loading}
                      className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.password ? 'border-error' : 'border-border-default'}`}
                      placeholder="Password"
                      autoComplete="current-password"
                    />
                    {errors.password && <p className="text-xs text-error mt-1">{errors.password}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !email || !password}
                    className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 font-normal flex items-center justify-center mb-4 active:scale-[0.97]"
                  >
                    {loading ? (
                      <Spinner size="sm" color="text-tertiary" />
                    ) : (
                      'Sign in'
                    )}
                  </button>

                  <div className="w-full flex justify-between items-center">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-sm text-secondary hover:text-primary transition-colors duration-150"
                    >
                      ← Back
                    </button>
                    
                    <Link
                      to="/forgot-password"
                      className="text-sm text-secondary hover:text-primary transition-colors duration-150"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </form>
              )}

              {phase === 'signup' && (
                <form onSubmit={(e) => { e.preventDefault(); handleSignUp(); }} className="flex flex-col items-center">
                  {errors.form && (
                    <p className="w-full text-xs text-error mb-4">{errors.form}</p>
                  )}
                  
                  <div className="w-full mb-4">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: null })) }}
                      disabled={loading}
                      className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.email ? 'border-error' : 'border-border-default'}`}
                      placeholder="you@example.com"
                      autoComplete="email"
                      autoFocus
                    />
                    {errors.email && <p className="text-xs text-error mt-1">{errors.email}</p>}
                  </div>

                  <div className="w-full mb-4">
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: null })) }}
                      disabled={loading}
                      className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.password ? 'border-error' : 'border-border-default'}`}
                      placeholder="Password (8+ characters)"
                      autoComplete="new-password"
                    />
                    {errors.password && <p className="text-xs text-error mt-1">{errors.password}</p>}
                  </div>

                  <div className="w-full mb-4">
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: null })) }}
                      disabled={loading}
                      className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.confirmPassword ? 'border-error' : 'border-border-default'}`}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                    />
                    {errors.confirmPassword && <p className="text-xs text-error mt-1">{errors.confirmPassword}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !email || !password || !confirmPassword}
                    className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 font-normal flex items-center justify-center mb-4 active:scale-[0.97]"
                  >
                    {loading ? (
                      <Spinner size="sm" color="text-tertiary" />
                    ) : (
                      'Create account'
                    )}
                  </button>

                  <div className="w-full flex justify-between items-center">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-sm text-secondary hover:text-primary transition-colors duration-150"
                    >
                      ← Back
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setPhase('signin')}
                      className="text-sm text-secondary hover:text-primary transition-colors duration-150"
                    >
                      Sign in
                    </button>
                  </div>
                </form>
              )}

              {phase === 'recovery' && recoveryKey && (
                <div />
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>

        </div>
      </div>

      {phase === 'recovery' && recoveryKey && (
        <RecoveryKeyModal
          recoveryKey={recoveryKey}
          onConfirm={handleRecoveryConfirm}
          onCancel={handleRecoveryCancel}
        />
      )}
    </>
  )
}

export default Login
